import { Injectable, Injector, signal } from '@angular/core';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  GlobalThemeConfiguration,
  generateGlobalThemesCssStringsFromThemeConfig,
  globalThemeConstant,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { Subject, takeUntil, tap } from 'rxjs';
import { AddNewGlobalThemeConfig, UpdateProjectThemes } from '../../actions/global-theme.action';
import { PublishProjectWorkflowConfig } from '../../actions/project.action';
import { GlobalThemeState } from '../../state/global-theme.state';
import { ProjectsState } from '../../state/project.state';
import { AutoDestroy, deepEqual } from '../../utils/utils';

/**
 * Theme paths a stored theme owns whole, rather than having the defaults merged into them, so a
 * default palette group or typography style the theme doesn't have isn't added back. Only for a
 * theme with `tokenOrder`: every save since it was introduced has written every default group and
 * style, so one missing was removed on purpose. An older theme may predate a default group (e.g.
 * `border`) and still needs it filled in.
 */
const USER_OWNED_THEME_PATHS = ['generalConfigurations.palette', 'typography'];

@Injectable({
  providedIn: 'root',
})
export class ThemeUtilityService {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public isThemeConfigurationChanged = signal<boolean>(false);
  public cssString: string;
  private activeThemeConfig: GlobalThemeConfiguration;
  public globalCssVariables = signal<Record<string, string>>({});
  public duplicateThemeExists = signal<boolean>(false);
  public importedThemeHasSyntaxError = signal<boolean>(false);
  public importedThemeHasMissingKeys = signal<boolean>(false);
  public reuploadThemeJson = signal<boolean>(false);
  public importedTheme = signal<GlobalThemeConfiguration>({} as GlobalThemeConfiguration);
  public importedThemeString = signal<string>('');

  constructor(
    private store: Store,
    private configService: ConfigService,
    private alertHandlerService: AlertHandlerService,
    private injector: Injector,
  ) {
    this.subscribeToGlobalThemeState();
  }

  private subscribeToGlobalThemeState() {
    this.store
      .select(GlobalThemeState.getGlobalThemeState)
      .pipe(
        takeUntil(this.destroy$),
        tap((res) => {
          if (res) {
            this.activeThemeConfig = res.themes[res.selectedThemeIndex];
            if (this.activeThemeConfig) this.generateCssFromActiveTheme(this.activeThemeConfig);
          }
        }),
      )
      .subscribe();
  }

  public updateProjectThemes() {
    const projectId = this.store.selectSnapshot(ProjectsState.getActiveProject)?.id;
    this.store.dispatch(new UpdateProjectThemes(projectId));
    this.setIsThemeConfigurationChanged(false);
  }

  public generateCssFromActiveTheme(theme: GlobalThemeConfiguration) {
    const css = this.generateCssString(theme);
    this.createStyleElementAndAppend(css);
    this.configService.globalCssVariables.set(this.loadGlobalCssVariables());
  }

  // to load css variables from style tag
  private loadGlobalCssVariables() {
    const styleElement = document.getElementById('globalTheme') as HTMLStyleElement;
    return this.extractRootVariables(styleElement?.textContent ?? '');
  }

  private extractRootVariables(cssContent: string): Record<string, string> {
    const rootMatches = cssContent.matchAll(/:root\s*{([^}]*)}/gs);
    const result: Record<string, string> = {};
    for (const match of rootMatches) {
      const rootContent = match[1];
      const variables = Array.from(rootContent.matchAll(/(--[\w-]+)\s*:\s*([^;]+);/g));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      variables.forEach(([_, name, value]) => {
        result[name.trim()] = value.trim(); // Later definitions overwrite earlier ones
      });
    }
    return result;
  }

  private createStyleElementAndAppend(cssString: string) {
    if (document.getElementById('globalTheme')) {
      document.getElementById('globalTheme')?.remove();
    }
    const styleElement = document.createElement('style');
    styleElement.setAttribute('id', 'globalTheme');
    styleElement.innerHTML = cssString;
    // Append the style element to the document's head
    document.head.appendChild(styleElement);
  }

  private generateCssString(theme: GlobalThemeConfiguration): string {
    return generateGlobalThemesCssStringsFromThemeConfig([theme], 0)[0].cssString;
  }

  public isThemeConfigChanged() {
    return this.isThemeConfigurationChanged();
  }

  public setIsThemeConfigurationChanged(value: boolean) {
    this.isThemeConfigurationChanged.set(value);
  }

  public updateGlobalTheme(activeGlobalThemeInitialData: GlobalThemeConfiguration) {
    this.updateProjectThemes();
    this.publishWorkflowConfigIfRequired(activeGlobalThemeInitialData);
    this.setIsThemeConfigurationChanged(false);
  }

  private publishWorkflowConfigIfRequired(activeGlobalThemeInitialData: GlobalThemeConfiguration) {
    const activeGlobalTheme = this.store.selectSnapshot(GlobalThemeState.getActiveGlobalTheme);
    const isCompanyInfoChanged = !deepEqual(
      activeGlobalTheme.generalConfigurations.companyInfo,
      activeGlobalThemeInitialData.generalConfigurations.companyInfo,
    );
    const isFontFamilyChanged =
      activeGlobalTheme.generalConfigurations.fontFamily !==
        activeGlobalThemeInitialData.generalConfigurations.fontFamily ||
      activeGlobalTheme.generalConfigurations.secondaryFontFamily !==
        activeGlobalThemeInitialData.generalConfigurations.secondaryFontFamily;
    if (isCompanyInfoChanged || isFontFamilyChanged) {
      this.store.dispatch(new PublishProjectWorkflowConfig({}, this.injector));
    }
  }
  public async uploadAndValidateJsonFile(file?: File, strigifiedThemeJson?: string): Promise<void> {
    try {
      const fileContent = file ? await file.text() : strigifiedThemeJson;
      const parsedJson = this.validateJsonSyntax(fileContent ?? '');
      this.validateJsonSchema(parsedJson);
    } catch (error) {
      console.error('Error during JSON validation:', error);
    }
  }

  /**
   * Parses a JSON string and returns the corresponding object. If the JSON string
   * is invalid, it identifies the line and column of the syntax error, logs an
   * error message, and throws an exception with detailed error information.
   *
   * @param jsonString - The JSON string to be parsed.
   * @returns The parsed JSON object if the syntax is valid.
   * @throws Will throw an error with detailed syntax error information if parsing fails.
   */
  private validateJsonSyntax(jsonString: string): any {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      if (error instanceof Error) {
        const positionMatch = /position (\d+)/.exec(error.message);
        let line = 0;
        let column = 0;

        if (positionMatch) {
          const position = parseInt(positionMatch[1], 10);
          const lines = jsonString.substring(0, position).split('\n');
          line = lines.length;
          column = lines[lines.length - 1].length;
        }

        const errorMessage = `Syntax Error at line ${line}, column ${column}: ${error.message}`;
        console.error(errorMessage);
        this.importedThemeString.set(jsonString);
        this.importedThemeHasSyntaxError.set(true);
        throw new Error(errorMessage);
      } else {
        console.error(error);
        throw error;
      }
    }
  }

  /**
   * Validates a JSON object against a reference schema, identifying any missing keys,
   * type mismatches, or invalid enum values. The validation process is recursive,
   * traversing nested objects and arrays. If validation errors are found, an error
   * is logged and an exception is thrown. Otherwise, a success message is logged.
   *
   * @param jsonData - The JSON data to validate.
   * @param referenceData - The reference schema to validate against, defaulting to `globalThemeConstant`.
   * @param path - The current path in the JSON structure, used for error reporting.
   * @throws Will throw an error if schema validation fails.
   */
  private validateJsonSchema(jsonData: any, referenceData = globalThemeConstant, path = ''): void {
    const errors: string[] = [];
    // The merge takes these whole for such a theme, so it can't fill a default key missing from them.
    const userOwnedPaths = 'tokenOrder' in jsonData ? USER_OWNED_THEME_PATHS : [];

    const validate = (data: any, refData: any, currentPath: string) => {
      Object.keys(refData).forEach((key) => {
        const fullPath = currentPath ? `${currentPath}.${key}` : key;
        if (userOwnedPaths.includes(fullPath)) return;
        if (!(key in data)) {
          errors.push(`Missing key: ${fullPath}, expected value type: ${typeof refData[key]}`);
        } else {
          const refValue = refData[key];
          const dataValue = data[key];
          if (typeof refValue === 'object' && refValue !== null && !Array.isArray(refValue)) {
            validate(dataValue, refValue, fullPath);
          } else if (Array.isArray(refValue) && !Array.isArray(dataValue)) {
            errors.push(`Type mismatch at ${fullPath}: expected array, got ${typeof dataValue}`);
          } else if (typeof refValue === 'string' && refValue.includes('|')) {
            const enumValues = refValue.split('|').map((v) => v.trim());
            if (!enumValues.includes(dataValue)) {
              errors.push(`Invalid enum value at ${fullPath}: got ${dataValue}`);
            }
          } else if (typeof dataValue !== typeof refValue) {
            errors.push(`Type mismatch at ${fullPath}: expected ${typeof refValue}, got ${typeof dataValue}`);
          }
        }
      });
    };

    validate(jsonData, referenceData, path);

    if (errors.length > 0) {
      this.importedTheme.set(jsonData as GlobalThemeConfiguration);
      this.importedThemeHasMissingKeys.set(true);
      // merge with default constant to add missing properties and validate again
      jsonData = this.deepMergeWithGlobalThemeConstant<GlobalThemeConfiguration>(
        jsonData,
        globalThemeConstant,
      );
      this.validateJsonSchema(jsonData);
    } else {
      this.proceedWithImportingTheme(jsonData as GlobalThemeConfiguration);
    }
  }

  private proceedWithImportingTheme(uploadedTheme: GlobalThemeConfiguration) {
    const globalThemes = this.store.selectSnapshot(GlobalThemeState.getGlobalThemeState);
    const duplicateThemeExists = globalThemes?.themes.some(
      (theme) => theme.themeName.toLowerCase() === uploadedTheme.themeName.toLowerCase(),
    );
    if (duplicateThemeExists) {
      this.importedTheme.set(uploadedTheme);
      this.duplicateThemeExists.set(true);
      return;
    }
    this.store.dispatch(new AddNewGlobalThemeConfig(uploadedTheme));
    this.alertHandlerService.showAlertFn(
      'success',
      'The theme configuration has been successfully imported.',
      'Import Successful',
    );
  }

  /**
   * Merges a target object with a source object, recursively combining properties.
   * If a property in the target is an object and the corresponding property in the source
   * is either undefined or also an object, the properties are merged. Otherwise, the target
   * property value is used. Arrays are not merged and are replaced by the target value.
   *
   * @template GlobalThemeConfiguration - The type of the configuration objects.
   * @param target - The target object to merge into the source.
   * @param source - The source object to merge with the target.
   * @returns A new object representing the merged result of the target and source.
   */
  public deepMergeWithGlobalThemeConstant<GlobalThemeConfiguration extends object>(
    target: Partial<GlobalThemeConfiguration>,
    source: GlobalThemeConfiguration,
    path = '',
    wholePaths: string[] = 'tokenOrder' in target ? USER_OWNED_THEME_PATHS : [],
  ): GlobalThemeConfiguration {
    const output = { ...source } as Record<string, unknown>;

    if (target && typeof target === 'object') {
      Object.keys(target).forEach((key) => {
        const targetValue = (target as Record<string, unknown>)[key];
        const sourceValue = (source as Record<string, unknown>)[key];
        const keyPath = path ? `${path}.${key}` : key;

        if (wholePaths.includes(keyPath) && targetValue && typeof targetValue === 'object') {
          output[key] = targetValue;
        } else if (key === 'foundations' && targetValue && typeof targetValue === 'object') {
          // Each saved token scale is user-owned: take it whole so a deleted default token stays deleted.
          output[key] = { ...(sourceValue as object), ...targetValue };
        } else if (targetValue && typeof targetValue === 'object' && !Array.isArray(targetValue)) {
          // If target value is an object (not array) and source value is either undefined or also an object
          // Only merge if sourceValue is an object or undefined
          if (sourceValue === undefined || (typeof sourceValue === 'object' && !Array.isArray(sourceValue))) {
            output[key] = this.deepMergeWithGlobalThemeConstant(
              targetValue as object,
              (sourceValue as object) || {},
              keyPath,
              wholePaths,
            );
          } else {
            // If sourceValue exists but is not an object, use targetValue
            output[key] = targetValue;
          }
        } else if (targetValue !== undefined) {
          // For non-object values or arrays, use the target value if it exists
          output[key] = targetValue;
        }
      });
    }
    return output as GlobalThemeConfiguration;
  }
}
